import { makeAutoObservable, runInAction } from 'mobx';
import type { CategoryId } from '@shared/config/categories';
import type { PartySnapshot, PartyEntry, PartyEvent, PartyCommand } from '@shared/api';
import type { RoomChannel, RoomHandlers } from '@shared/lib/room-socket';

const kinds: Record<CategoryId, string> = { movies: 'film', series: 'series', games: 'game', anime: 'anime' };
const categoryByKind: Record<string, CategoryId> = { film: 'movies', series: 'series', game: 'games', anime: 'anime' };
type Transport = {
  create: (body: {
    displayName: string;
    screenOnly: boolean;
    questionCount: number;
    kinds: string[];
  }) => Promise<PartyEntry>;
  join: (code: string, name: string) => Promise<PartyEntry>;
  snapshot: (code: string) => Promise<PartySnapshot>;
  open: (code: string, handlers: RoomHandlers<PartyEvent>) => RoomChannel<PartyCommand>;
  actionId: () => string;
};
const errors: Record<string, string> = {
  NOT_ENOUGH_QUESTIONS: 'В выбранных категориях недостаточно вопросов для этой игры.',
  NOT_ALL_READY: 'Не все участники готовы.',
  NOT_READY: 'Не все участники готовы.',
  NEED_TEAM: 'Выберите команду перед подтверждением готовности.',
  NOT_ENOUGH_PLAYERS: 'Для начала игры нужны хотя бы два игрока.',
  TEAM_LIMIT: 'В комнате уже создано десять команд.',
  TEAM_NOT_FOUND: 'Команда больше недоступна. Выберите другую.',
  BAD_SETTINGS: 'Проверьте категории и количество вопросов.',
  WRONG_PHASE: 'Этап игры уже изменился. Обновляем комнату.',
  WRONG_QUESTION: 'Вопрос уже изменился. Обновляем комнату.',
  TIME_UP: 'Время на ответ закончилось.',
  ALREADY_ANSWERED: 'Ответ уже принят. Обновляем комнату.',
  ROOM_FULL: 'В комнате нет свободных мест.',
  ROOM_CLOSED: 'Комната закрыта.',
  ROOM_NOT_FOUND: 'Комната не найдена. Создайте новую или проверьте код приглашения.',
  GAME_STARTED: 'Игра уже началась. Новый вход закрыт.',
  FORBIDDEN: 'Это действие недоступно.',
  TIME_EXPIRED: 'Время на ответ закончилось.',
};
export class PartyRoomStore {
  state: PartySnapshot | null = null;
  categories: CategoryId[] = [];
  questionCount = 10;
  displayOnly = false;
  readonly duration = 30;
  connecting = false;
  syncing = false;
  connection: 'idle' | 'connecting' | 'open' | 'closed' = 'idle';
  reconnectBlocked = false;
  retryGeneration = 0;
  error: string | null = null;
  frameReady = false;
  remainingMs = 0;
  revealRemainingMs = 0;
  ownerLogin = '';
  private participantId = '';
  private revision = 0;
  private streamVersion = 0;
  private serial = 0;
  private offset = 0;
  private channel: RoomChannel<PartyCommand> | null = null;
  private request: Promise<void> | null = null;
  private pending = new Map<string, PartyCommand>();
  private answerPending: { questionId: string; titleId: string; actionId: string; command: PartyCommand } | null = null;
  private acceptedAnswer: { questionId: string; titleId: string } | null = null;
  private readonly transport: Transport;
  private readonly viewer: () => string | undefined;
  constructor(transport: Transport, viewer: () => string | undefined) {
    this.transport = transport;
    this.viewer = viewer;
    makeAutoObservable<this, 'transport' | 'viewer' | 'channel' | 'request'>(
      this,
      { transport: false, viewer: false, channel: false, request: false },
      { autoBind: true },
    );
  }
  get phase() {
    const phase = this.state?.phase;
    return phase === 'preparing' ? 'loading' : (phase ?? 'setup');
  }
  get code() {
    return this.state?.code ?? '';
  }
  get connectionAttempt() {
    return this.serial;
  }
  get selfId() {
    return this.participantId;
  }
  get isMaster() {
    return this.state?.masterParticipantId === this.selfId;
  }
  get screenOnly() {
    return this.isMaster && this.displayOnly;
  }
  get teamMode() {
    return !!this.state?.teams.length;
  }
  get teams() {
    return this.state?.teams ?? [];
  }
  get players() {
    return (this.state?.players ?? []).map((p) => ({
      id: p.participantId,
      name: p.displayName,
      ready: p.ready,
      connected: p.connected,
      active: p.active,
      teamId: p.teamId ?? null,
      screenOnly: p.screenOnly,
    }));
  }
  get participants() {
    return this.players.filter((p) => !p.screenOnly && !(this.isMaster && this.screenOnly && p.id === this.selfId));
  }
  get self() {
    return this.participants.find((p) => p.id === this.selfId);
  }
  get readyCount() {
    return this.participants.filter((p) => p.ready && p.connected).length;
  }
  get canStart() {
    return (
      this.phase === 'lobby' &&
      this.isMaster &&
      this.connection === 'open' &&
      this.participants.length >= 2 &&
      this.participants.every((p) => p.ready && p.connected && (!this.teamMode || p.teamId))
    );
  }
  get round() {
    return Math.max(0, (this.state?.question?.index ?? 1) - 1);
  }
  get question() {
    const q = this.state?.question;
    if (!q) return null;
    return {
      id: q.id,
      category: categoryByKind[q.kind],
      difficulty: q.difficulty,
      screenshots: q.screenshots?.length ? q.screenshots : q.screenshot ? [q.screenshot] : [],
      options: q.options.map((o) => o.name),
      optionTitleIds: q.options.map((o) => o.titleId),
    };
  }
  get acceptedTitleId() {
    return (
      this.state?.yourAnswerTitleId ||
      (this.acceptedAnswer?.questionId === this.question?.id ? this.acceptedAnswer?.titleId : undefined)
    );
  }
  get selectedAnswer() {
    const titleId = this.acceptedTitleId ?? this.answerPending?.titleId;
    return titleId ? (this.question?.optionTitleIds.indexOf(titleId) ?? -1) : -1;
  }
  get answered() {
    return !!this.acceptedTitleId || !!this.answerPending;
  }
  get answeredCount() {
    return this.state?.answeredCount ?? 0;
  }
  get participantCount() {
    return this.state?.participantCount ?? this.participants.filter((p) => p.active).length;
  }
  get correctTitleId() {
    return this.phase === 'reveal' ? this.state?.correctTitleId : undefined;
  }
  get revealSecondsLeft() {
    return Math.ceil(this.revealRemainingMs / 1000);
  }
  get timedOut() {
    return !!this.state?.questionDeadlineAt && this.remainingMs <= 0;
  }
  get results() {
    if (this.phase !== 'finished' || !this.state?.results) return [];
    return this.teamMode
      ? this.state.results.teams.map((t) => ({ id: t.teamId, name: t.teamName, score: t.score, place: t.place }))
      : this.state.results.players.map((p) => ({
          id: p.participantId,
          name: p.displayName,
          score: p.score,
          place: p.place,
        }));
  }
  get resultTitle() {
    const leaders = this.results.filter((r) => r.place === 1);
    return leaders.length > 1
      ? 'Победу разделили несколько участников'
      : leaders[0]
        ? `${leaders[0].name} — победитель!`
        : 'Игра завершена';
  }
  private active(revision: number, login = this.ownerLogin) {
    return revision === this.revision && login === this.viewer();
  }
  applySnapshot(snapshot: PartySnapshot) {
    if (this.state && (snapshot.roomId !== this.state.roomId || snapshot.revision < this.state.revision)) return;
    const previous = this.state?.question;
    if (
      previous?.id !== snapshot.question?.id ||
      previous?.index !== snapshot.question?.index ||
      this.state?.gameId !== snapshot.gameId
    ) {
      this.frameReady = false;
      this.answerPending = null;
      this.acceptedAnswer = null;
      this.remainingMs = snapshot.questionDeadlineAt
        ? Math.max(0, Date.parse(snapshot.questionDeadlineAt) - Date.parse(snapshot.serverNow))
        : 0;
    }
    this.state = snapshot;
    this.participantId = snapshot.you?.participantId ?? this.participantId;
    this.categories = snapshot.kinds.flatMap((k) => (categoryByKind[k] ? [categoryByKind[k]] : []));
    this.questionCount = snapshot.questionCount;
    this.displayOnly = snapshot.screenOnly;
    this.offset = Date.parse(snapshot.serverNow) - Date.now();
    if (snapshot.yourAnswerTitleId) {
      this.answerPending = null;
      this.acceptedAnswer = null;
    }
    this.error = null;
    this.tick();
    if (snapshot.phase === 'closed') this.closeChannel();
  }
  async enter(available: readonly CategoryId[], name: string, code?: string) {
    if (this.connecting || !name.trim()) return false;
    const selected = this.categories.filter((id) => available.includes(id));
    if (!code && !selected.length) {
      this.error = 'Выберите хотя бы одну категорию.';
      return false;
    }
    const login = this.viewer();
    if (!login) return false;
    this.leave();
    const revision = this.revision;
    this.ownerLogin = login;
    this.connecting = true;
    try {
      let entry: PartyEntry;
      if (code) {
        // Snapshot first restores an existing participant without another join.
        let state: PartySnapshot | undefined;
        try {
          state = await this.transport.snapshot(code);
        } catch (error) {
          if ((error as { status?: number })?.status !== 403) throw error;
        }
        if (!this.active(revision, login)) return false;
        if (state?.you)
          entry = { code, participantId: state.you.participantId, isMaster: state.you.isMaster, snapshot: state };
        else entry = await this.transport.join(code, name.trim());
      } else
        entry = await this.transport.create({
          displayName: name.trim(),
          screenOnly: this.displayOnly,
          questionCount: this.questionCount,
          kinds: selected.map((id) => kinds[id]),
        });
      if (!this.active(revision, login)) return false;
      if (
        entry.snapshot.kinds.some((kind) => {
          const category = categoryByKind[kind];
          return !category || !available.includes(category);
        })
      )
        throw new Error('Disabled room categories');
      runInAction(() => {
        this.participantId = entry.participantId;
        this.applySnapshot(entry.snapshot);
      });
      this.connectSocket();
      return true;
    } catch (error) {
      runInAction(() => {
        if (this.active(revision, login)) this.error = this.message(error);
      });
      return false;
    } finally {
      runInAction(() => {
        if (this.active(revision, login)) this.connecting = false;
      });
    }
  }
  private message(error: unknown) {
    const code = (error as { data?: { code?: string; error?: string } })?.data;
    return errors[code?.code ?? code?.error ?? ''] ?? 'Не удалось выполнить действие Party. Повторите попытку.';
  }
  connectSocket() {
    if (
      !this.code ||
      this.reconnectBlocked ||
      this.phase === 'closed' ||
      this.connection === 'connecting' ||
      this.connection === 'open'
    )
      return;
    const revision = this.revision,
      serial = ++this.serial;
    const active = () => this.active(revision) && serial === this.serial;
    let restoring = true;
    this.channel?.close();
    this.connection = 'connecting';
    try {
      this.channel = this.transport.open(this.code, {
        open: () =>
          runInAction(() => {
            if (active()) {
              this.connection = 'open';
              this.error = null;
            }
          }),
        close: () =>
          runInAction(() => {
            if (active()) this.connection = 'closed';
          }),
        event: (event) => {
          if (active()) {
            this.receive(event);
            if (restoring && event.snapshot) {
              restoring = false;
              this.replayAnswer();
            }
          }
        },
        invalid: () =>
          runInAction(() => {
            if (active()) this.error = 'Не удалось прочитать событие Party.';
          }),
      });
    } catch {
      this.connection = 'closed';
      this.error = 'Не удалось подключить игровой канал Party.';
    }
  }
  receive(event: PartyEvent) {
    if (event.roomId && event.roomId !== this.state?.roomId) return;
    if (!event.snapshot && event.gameId && this.state?.gameId && event.gameId !== this.state.gameId) return;
    if (!event.snapshot && event.questionId && event.questionId !== this.state?.question?.id) return;
    if (event.revision !== undefined && this.state && event.revision < this.state.revision) return;
    this.streamVersion++;
    if (event.type === 'error') {
      if (['ROOM_NOT_FOUND', 'ROOM_CLOSED'].includes(event.code ?? '')) {
        this.closeRoom(errors[event.code!]!);
        return;
      }
      if (event.actionId) this.pending.delete(event.actionId);
      this.error = errors[event.code ?? ''] ?? event.message ?? 'Сервер не принял действие.';
      if (event.actionId === this.answerPending?.actionId) {
        this.answerPending = null;
        this.tick();
      }
      if (['WRONG_PHASE', 'WRONG_QUESTION', 'ALREADY_ANSWERED', 'TIME_UP'].includes(event.code ?? ''))
        void this.refresh();
      return;
    }
    if (event.actionId) {
      this.error = null;
      this.pending.delete(event.actionId);
      if (this.answerPending?.actionId === event.actionId) {
        this.acceptedAnswer = { questionId: this.answerPending.questionId, titleId: this.answerPending.titleId };
        this.answerPending = null;
      }
    }
    if (event.snapshot) {
      this.applySnapshot(event.snapshot);
      return;
    }
    if (this.state && ['ack', 'answer_received'].includes(event.type)) {
      const gap = event.revision !== undefined && event.revision > this.state.revision + 1;
      if (event.answeredCount !== undefined || event.participantCount !== undefined) {
        this.state = {
          ...this.state,
          revision: event.revision ?? this.state.revision,
          ...(event.answeredCount !== undefined ? { answeredCount: event.answeredCount } : {}),
          ...(event.participantCount !== undefined ? { participantCount: event.participantCount } : {}),
        };
      }
      if (event.serverNow && Number.isFinite(Date.parse(event.serverNow))) {
        this.offset = Date.parse(event.serverNow) - Date.now();
        this.tick();
      }
      if (gap) void this.refresh();
    }
    if (event.type === 'room_closed') {
      this.closeRoom('Комната закрыта.');
    }
  }
  private command(type: PartyCommand['type'], payload?: Record<string, unknown>) {
    if (this.connection !== 'open') {
      this.error = 'Восстанавливаем соединение с комнатой.';
      return null;
    }
    const command: PartyCommand = {
      type,
      actionId: this.transport.actionId(),
      ...(this.state?.gameId ? { gameId: this.state.gameId } : {}),
      ...(payload ? { payload } : {}),
    };
    this.pending.set(command.actionId, command);
    if (!this.channel?.send(command)) this.connection = 'closed';
    return command;
  }
  setReady(id: string, ready: boolean) {
    if (this.phase === 'lobby' && id === this.selfId) this.command('set_ready', { ready });
  }
  setDisplayOnly(value: boolean) {
    if (this.phase === 'setup') this.displayOnly = value;
    else if (this.isMaster && this.phase === 'lobby') this.command('set_screen_only', { screenOnly: value });
  }
  setTeamMode(value: boolean) {
    if (!this.isMaster || this.phase !== 'lobby') return;
    if (value && !this.teams.length) {
      this.addTeam();
      this.addTeam('Команда 2');
    } else if (!value) this.teams.forEach((team) => this.removeTeam(team.id));
  }
  addTeam(name = `Команда ${this.teams.length + 1}`) {
    if (this.isMaster && this.phase === 'lobby') this.command('create_team', { name });
  }
  renameTeam(teamId: string, name: string) {
    if (this.isMaster && this.phase === 'lobby' && name.trim())
      this.command('rename_team', { teamId, name: name.trim() });
  }
  removeTeam(teamId: string) {
    if (this.isMaster && this.phase === 'lobby') this.command('delete_team', { teamId });
  }
  setPlayerTeam(participantId: string, teamId: string | null) {
    if (this.phase === 'lobby' && !this.screenOnly && participantId === this.selfId)
      this.command('set_team', { teamId: teamId ?? '' });
  }
  start() {
    if (this.canStart) this.command('start');
  }
  advance() {
    if (this.isMaster && this.phase === 'reveal') this.command('advance');
  }
  returnToLobby() {
    if (this.isMaster && this.phase === 'finished') this.command('return_to_lobby');
  }
  setQuestionCount(value: number | string) {
    if (this.phase === 'setup') this.questionCount = Math.min(50, Math.max(10, Math.round(Number(value) || 10)));
  }
  toggleCategory(id: CategoryId, available: readonly CategoryId[]) {
    if (this.phase !== 'setup') return;
    const selected = this.categories.filter((id) => available.includes(id));
    this.categories = selected.includes(id) ? selected.filter((c) => c !== id) : [...selected, id];
  }
  markFrameReady() {
    this.frameReady = true;
  }
  answer(option: number) {
    const q = this.question,
      titleId = q?.optionTitleIds[option];
    if (
      !q ||
      !titleId ||
      this.phase !== 'question' ||
      !this.frameReady ||
      this.answered ||
      this.screenOnly ||
      !this.self?.active ||
      this.timedOut
    )
      return;
    const command = this.command('answer', { questionId: q.id, titleId });
    if (command) this.answerPending = { questionId: q.id, titleId, actionId: command.actionId, command };
  }
  private replayAnswer() {
    const pending = this.answerPending;
    if (
      pending &&
      pending.questionId === this.question?.id &&
      !this.acceptedTitleId &&
      this.phase === 'question' &&
      !this.timedOut &&
      this.connection === 'open'
    ) {
      if (!this.channel?.send(pending.command)) this.connection = 'closed';
    }
  }
  tick(now = Date.now()) {
    if (!this.state) return;
    const time = now + this.offset;
    if (!this.answered && this.state.questionDeadlineAt)
      this.remainingMs = Math.max(0, Date.parse(this.state.questionDeadlineAt) - time);
    if (this.state.nextQuestionAt) this.revealRemainingMs = Math.max(0, Date.parse(this.state.nextQuestionAt) - time);
  }
  refresh(): Promise<void> {
    if (this.request) return this.request;
    if (!this.code) return Promise.resolve();
    const code = this.code,
      revision = this.revision,
      stream = this.streamVersion;
    this.syncing = true;
    const request = (async () => {
      await Promise.resolve();
      try {
        const state = await this.transport.snapshot(code);
        runInAction(() => {
          if (this.active(revision)) this.applySnapshot(state);
        });
      } catch (error) {
        runInAction(() => {
          if (this.active(revision) && stream === this.streamVersion) {
            const failure = error as { status?: number; data?: { code?: string; error?: string } };
            if (
              [403, 404, 410].includes(failure.status ?? 0) ||
              ['ROOM_NOT_FOUND', 'ROOM_CLOSED'].includes(failure.data?.code ?? failure.data?.error ?? '')
            )
              this.closeRoom(this.message(error));
            else this.error = this.message(error);
          }
        });
      } finally {
        runInAction(() => {
          if (this.active(revision)) this.syncing = false;
          if (this.request === request) this.request = null;
        });
      }
    })();
    this.request = request;
    return request;
  }
  exit() {
    if (this.connection === 'open') this.command('leave');
    this.leave();
  }
  private closeRoom(message: string) {
    if (this.state) this.state = { ...this.state, phase: 'closed' };
    this.reconnectBlocked = true;
    this.error = message;
    this.closeChannel();
  }
  stopReconnect() {
    if (this.reconnectBlocked) return;
    this.reconnectBlocked = true;
    this.error = 'Не удалось восстановить связь с комнатой. Повторите подключение.';
    this.closeChannel();
  }
  async retryConnection() {
    if (this.phase === 'closed') return;
    this.reconnectBlocked = false;
    this.retryGeneration++;
    this.error = null;
    await this.refresh();
    this.connectSocket();
  }
  private closeChannel() {
    this.serial++;
    this.channel?.close();
    this.channel = null;
    this.connection = 'idle';
  }
  leave() {
    this.revision++;
    this.closeChannel();
    this.state = null;
    this.error = null;
    this.ownerLogin = '';
    this.participantId = '';
    this.connecting = false;
    this.syncing = false;
    this.request = null;
    this.pending.clear();
    this.answerPending = null;
    this.acceptedAnswer = null;
    this.frameReady = false;
    this.reconnectBlocked = false;
  }
}
